// Optional look of the offer documents. Colour, typeface and pages are chosen
// in the PDF preview, where the result is seen at once (OfferColorPicker is
// used there); the implementation steps need typing and stay in the form.
// Every default reproduces the document as it was before these options.
import {
  Accordion, AccordionDetails, AccordionSummary, Box, Button, FormControlLabel, Grid, IconButton,
  Stack, Switch, TextField, Tooltip, Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CheckIcon from "@mui/icons-material/Check";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {
  DEFAULT_BRAND_COLOR, OFFER_COLOR_PRESETS, PROPOSAL_STEP_LIMITS, defaultProposalSteps,
} from "../../components/config/commercialOffer";

const HEX = /^#[0-9a-f]{6}$/;

// The colour choice, used in the form and in the preview: preset swatches,
// a colour picker and a #RRGGBB field. value "" is the default teal.
export function OfferColorPicker({ value, onChange, isGreek, disabled }) {
  const tr = (el, en) => (isGreek ? el : en);
  const colour = value || "";
  const preset = OFFER_COLOR_PRESETS.find((p) => p.value === colour || (!colour && !p.value));
  const custom = Boolean(colour) && !preset;
  const customSwatch = HEX.test(colour) ? colour : "#ffffff";
  return (
    <>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap role="radiogroup" aria-label={tr("Χρώμα προσφοράς", "Offer colour")}>
        {OFFER_COLOR_PRESETS.map((p) => {
          const selected = p === preset;
          const label = isGreek ? p.el : p.en;
          return (
            <Tooltip key={p.swatch} title={label}>
              <span>
                <IconButton role="radio" aria-checked={selected} aria-label={label} disabled={disabled}
                  onClick={() => onChange(p.value)}
                  sx={{ width: 34, height: 34, bgcolor: p.swatch, border: "2px solid", borderColor: selected ? "text.primary" : "transparent",
                    boxShadow: selected ? "0 0 0 2px #fff inset" : "none", "&:hover": { bgcolor: p.swatch, opacity: 0.85 } }}>
                  {selected ? <CheckIcon sx={{ color: "#fff", fontSize: 18 }} /> : null}
                </IconButton>
              </span>
            </Tooltip>
          );
        })}
      </Stack>
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 1.5 }}>
        <Box component="label" sx={{ position: "relative", width: 34, height: 34, borderRadius: "50%", flex: "none",
          border: "2px solid", borderColor: custom ? "text.primary" : "divider", cursor: disabled ? "default" : "pointer",
          background: custom ? customSwatch : "conic-gradient(#e53935, #fdd835, #43a047, #1e88e5, #8e24aa, #e53935)" }}>
          <Box component="input" type="color" disabled={disabled} aria-label={tr("Επιλογή δικού σας χρώματος", "Pick your own colour")}
            value={HEX.test(colour) ? colour : DEFAULT_BRAND_COLOR}
            onChange={(e) => onChange(e.target.value.toLowerCase())}
            sx={{ position: "absolute", inset: 0, opacity: 0, cursor: "inherit", width: "100%", height: "100%" }} />
        </Box>
        <TextField size="small" disabled={disabled} label={tr("Δικό σας χρώμα", "Your own colour")} placeholder="#1f5fae"
          value={custom ? colour : ""} inputProps={{ maxLength: 7, spellCheck: false }} sx={{ width: 170 }}
          error={custom && !HEX.test(colour)}
          onChange={(e) => {
            const typed = e.target.value.trim();
            onChange(typed && !typed.startsWith("#") ? `#${typed}` : typed);
          }} />
      </Stack>
    </>
  );
}

export default function CommercialProposalAppearance({ value, onChange, isGreek, disabled, reportLanguage }) {
  const tr = (el, en) => (isGreek ? el : en);
  const ownSteps = value.steps.length > 0;
  const updateStep = (index, field, text) => onChange({
    steps: value.steps.map((step, i) => (i === index ? { ...step, [field]: text } : step)),
  });

  return (
    <Accordion>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography>{tr("Βήματα υλοποίησης αναλυτικής προσφοράς", "Implementation steps of the detailed proposal")}</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Stack spacing={2}>
          <Box>
            <FormControlLabel control={<Switch disabled={disabled} checked={ownSteps}
              onChange={(e) => onChange({ steps: e.target.checked ? defaultProposalSteps(reportLanguage) : [] })} />}
              label={tr("Δικά σας βήματα υλοποίησης", "Your own implementation steps")} />
            <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
              {ownSteps
                ? tr(`Από 1 έως ${PROPOSAL_STEP_LIMITS.count} βήματα, με τη σειρά που θα εμφανιστούν. Κενά βήματα παραλείπονται.`,
                  `1 to ${PROPOSAL_STEP_LIMITS.count} steps, in the order they will appear. Empty steps are left out.`)
                : tr("Ανενεργό: τα 4 βήματα της προσφοράς (Συμφωνία, Προετοιμασία, Εγκατάσταση, Παράδοση).",
                  "Off: the offer's 4 steps (Agreement, Preparation, Installation, Handover).")}
            </Typography>
            {ownSteps ? (
              <Stack spacing={1} sx={{ mt: 1 }}>
                {value.steps.map((step, index) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <Grid container spacing={1} key={index} alignItems="flex-start">
                    <Grid size={{ xs: 12, md: 4 }}>
                      <TextField fullWidth size="small" disabled={disabled} label={`${tr("Βήμα", "Step")} ${index + 1}`} value={step.title}
                        inputProps={{ maxLength: PROPOSAL_STEP_LIMITS.title }} onChange={(e) => updateStep(index, "title", e.target.value)} />
                    </Grid>
                    <Grid size={{ xs: 10, md: 7 }}>
                      <TextField fullWidth multiline maxRows={4} size="small" disabled={disabled} label={tr("Περιγραφή", "Description")} value={step.text}
                        inputProps={{ maxLength: PROPOSAL_STEP_LIMITS.text }} helperText={`${step.text.length}/${PROPOSAL_STEP_LIMITS.text}`}
                        onChange={(e) => updateStep(index, "text", e.target.value)} />
                    </Grid>
                    <Grid size={{ xs: 2, md: 1 }}>
                      <Tooltip title={tr("Αφαίρεση βήματος", "Remove step")}>
                        <span>
                          <IconButton aria-label={`${tr("Αφαίρεση βήματος", "Remove step")} ${index + 1}`} disabled={disabled || value.steps.length <= 1}
                            onClick={() => onChange({ steps: value.steps.filter((_, i) => i !== index) })}>
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Grid>
                  </Grid>
                ))}
                <Box>
                  <Button disabled={disabled || value.steps.length >= PROPOSAL_STEP_LIMITS.count}
                    onClick={() => onChange({ steps: [...value.steps, { title: "", text: "" }] })}>
                    {tr("Προσθήκη βήματος", "Add step")}
                  </Button>
                </Box>
              </Stack>
            ) : null}
          </Box>

        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}
